import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, ChevronDown } from "lucide-react";
import EmberCanvas from "@/components/EmberCanvas";
import EmberAudio from "@/components/EmberAudio";
import StarfieldCanvas from "@/components/StarfieldCanvas";
import HeroGlobe from "@/components/HeroGlobe";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";
import { ARTICLES, CRAFT_PILLARS, EMBLEM_URL, IMG, TERRITORIA } from "@/data/content";

const introPlayed = () => {
  try {
    return sessionStorage.getItem("af-intro") === "1";
  } catch {
    return false;
  }
};

const Hero = () => {
  const ref = useRef(null);
  const base = introPlayed() ? 0.2 : 2.1;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const emblemY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} data-testid="home-hero" className="noise-overlay relative flex min-h-screen flex-col items-center justify-center overflow-hidden">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img
          src={IMG.heroLandscape}
          alt=""
          className="h-[120%] w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0B0D]/70 via-[#0A0B0D]/55 to-[#0A0B0D]" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 55% 45% at 50% 42%, rgba(229,46,46,0.12) 0%, transparent 70%)",
          }}
        />
      </motion.div>
      <EmberCanvas density={34} firebed className="absolute inset-0 h-full w-full" />
      <EmberAudio />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col items-center px-6 text-center">
        <motion.div style={{ y: emblemY }}>
          <motion.img
            src={EMBLEM_URL}
            alt="Arsenal Forge emblem"
            data-testid="hero-emblem"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: base, duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="emblem-glow w-32 animate-ember-pulse sm:w-40 lg:w-44"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: base + 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 font-mono text-xs tracking-[0.4em] text-[#E52E2E] sm:text-sm"
        >
          ARSENAL FORGE
        </motion.p>

        <h1 className="mt-5 font-display text-6xl font-black uppercase leading-[0.95] tracking-tight text-[#F4F5F7] sm:text-8xl lg:text-[9rem]">
          {["We Forge", "Worlds."].map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ delay: base + 0.28 + i * 0.14, duration: 0.95, ease: [0.16, 1, 0.3, 1] }}
                className={`block ${i === 1 ? "text-[#E52E2E]" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: base + 0.65, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 max-w-md text-sm leading-relaxed text-[#9EA4B0] sm:text-base"
        >
          Independent game development studio. Plugins, addons, admin apps — and worlds.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: base + 0.8, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <Link
            to="/projects"
            data-testid="cta-explore-territoria"
            className="group flex items-center gap-3 bg-[#E52E2E] px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white shadow-[0_0_28px_rgba(229,46,46,0.25)] transition-colors duration-300 hover:bg-[#FF3B3B]"
          >
            See Our Work
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="cta-enter-the-forge"
            className="border border-[#3A404E] px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
          >
            Work With Us
          </Link>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: base + 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <ChevronDown className="animate-bounce text-[#5F6573]" size={22} />
      </motion.div>
    </section>
  );
};

const Capabilities = () => (
  <section data-testid="capabilities" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
    <Reveal>
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">WHAT THE FORGE BUILDS</p>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            Serious engineering, forged in production
          </h2>
        </div>
        <Link
          to="/contact"
          data-testid="capabilities-cta"
          className="group flex items-center gap-2 font-mono text-xs font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
        >
          COMMISSION THE FORGE
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>
    </Reveal>
    <div className="mt-14 grid gap-6 md:grid-cols-3">
      {[
        {
          code: "01",
          title: "PLUGIN & ADDON ENGINEERING",
          body: "Custom Oxide plugins for Rust and native addons for World of Warcraft — statistics engines, identity systems, in-game interfaces and balance tooling, proven live at scale.",
          proof: "8+ PROPRIETARY SYSTEMS IN PRODUCTION",
          to: "/projects",
          testid: "capability-plugins",
        },
        {
          code: "02",
          title: "SERVER OPS & ADMIN TOOLING",
          body: "Server administration apps, telemetry bridges, release pipelines and network management — the operational machinery that keeps a five-server community stable.",
          proof: "5-SERVER LIVE NETWORK, GOLD MASTER PIPELINE",
          to: "/plugins",
          testid: "capability-ops",
        },
        {
          code: "03",
          title: "GAME DEVELOPMENT",
          body: "Full-scale world development on our own engine — currently forging Territoria, a persistent geopolitical MMO with a live pioneer server.",
          proof: "TERRITORIA — OPEN BETA 01.01.2027",
          to: "/games",
          testid: "capability-games",
        },
      ].map((c, i) => (
        <Reveal key={c.code} delay={0.1 * i}>
          <Link
            to={c.to}
            data-testid={c.testid}
            className="group steel-panel relative flex h-full flex-col p-8 transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/50 hover:shadow-[0_0_40px_rgba(229,46,46,0.08)]"
          >
            <p className="font-mono text-sm tracking-[0.3em] text-[#5F6573] transition-colors duration-500 group-hover:text-[#E52E2E]">
              {c.code}
            </p>
            <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7]">
              {c.title}
            </h3>
            <p className="mt-4 flex-1 text-sm leading-relaxed text-[#9EA4B0]">{c.body}</p>
            <p className="mt-6 border-t border-[#282D37] pt-4 font-mono text-[10px] tracking-[0.25em] text-[#E52E2E]">
              {c.proof}
            </p>
            <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const TerritoriaSpotlight = () => (
  <section data-testid="territoria-spotlight" className="relative overflow-hidden border-y border-[#282D37]/60 bg-[#05070C]">
    <StarfieldCanvas density={140} className="absolute inset-0 h-full w-full" />
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        background:
          "radial-gradient(ellipse 55% 55% at 28% 50%, rgba(58,100,190,0.12) 0%, transparent 65%), radial-gradient(ellipse 40% 40% at 80% 75%, rgba(229,46,46,0.06) 0%, transparent 70%)",
      }}
    />
    <div className="relative mx-auto max-w-[1400px] px-6 py-28 lg:px-10 lg:py-36">
    <Reveal>
      <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FLAGSHIP TITLE // IN DEVELOPMENT</p>
    </Reveal>
    <div className="mt-10 grid grid-cols-1 items-center gap-12 [&>*]:min-w-0 lg:grid-cols-2 lg:gap-20">
      <Reveal delay={0.1}>
        <div data-testid="spotlight-globe" className="relative flex min-w-0 justify-center">
          <HeroGlobe />
          <div className="absolute bottom-2 left-0 flex items-center gap-3">
            <span className="h-2 w-2 animate-ember-pulse rounded-full bg-[#E52E2E]" />
            <span className="font-mono text-xs tracking-[0.3em] text-white">TERRITORIA // 01</span>
          </div>
        </div>
      </Reveal>
      <div>
        <Reveal delay={0.15}>
          <h2 className="font-display text-5xl font-black uppercase leading-none tracking-tight text-[#F4F5F7] sm:text-6xl lg:text-7xl">
            Territoria
          </h2>
        </Reveal>
        <Reveal delay={0.22}>
          <p className="mt-6 font-display text-xl font-semibold uppercase leading-snug tracking-wide text-[#C6CAD2] sm:text-2xl">
            {TERRITORIA.tagline}
          </p>
          <p className="mt-4 text-base leading-relaxed text-[#9EA4B0] sm:text-lg">
            A persistent multiplayer political, economic and military simulator. Build an economy,
            lead a government, command armies — and alter the course of history.
          </p>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 grid grid-cols-2 gap-px border border-[#282D37] bg-[#282D37]">
            {[
              ["GENRE", "GEOPOLITICAL MMO"],
              ["OPEN BETA", "01.01.2027"],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#121418] px-5 py-4">
                <p className="font-mono text-[10px] tracking-[0.3em] text-[#5F6573]">{k}</p>
                <p className="mt-1 font-mono text-xs font-bold tracking-widest text-[#F4F5F7]">{v}</p>
              </div>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.38}>
          <Link
            to="/games/territoria"
            data-testid="spotlight-cta-dossier"
            className="group mt-10 inline-flex items-center gap-3 border border-[#3A404E] px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
          >
            View Project Dossier
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </div>
    </div>
  </section>
);

const CraftPillars = () => (
  <section data-testid="craft-pillars" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
    <div className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
      <Reveal>
        <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE CRAFT</p>
        <h2 className="mt-4 max-w-2xl font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
          Forged, not manufactured
        </h2>
      </Reveal>
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {CRAFT_PILLARS.map((p, i) => (
          <Reveal key={p.code} delay={0.1 * i}>
            <div
              data-testid={`pillar-card-${p.code}`}
              className="group steel-panel relative h-full p-8 transition-[border-color,box-shadow] duration-500 hover:border-[#E52E2E]/50 hover:shadow-[0_0_40px_rgba(229,46,46,0.08)]"
            >
              <p className="font-mono text-sm tracking-[0.3em] text-[#5F6573] transition-colors duration-500 group-hover:text-[#E52E2E]">
                {p.code}
              </p>
              <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7]">
                {p.title}
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-[#9EA4B0]">{p.body}</p>
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const LatestDispatches = () => {
  const latest = ARTICLES.slice(0, 3);
  return (
    <section data-testid="latest-dispatches" className="mx-auto max-w-[1400px] px-6 py-28 lg:px-10">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FORGE JOURNAL</p>
            <h2 className="mt-4 font-display text-4xl font-black uppercase tracking-tight text-[#F4F5F7] sm:text-5xl">
              Latest dispatches
            </h2>
          </div>
          <Link
            to="/journal"
            data-testid="dispatches-view-all"
            className="group flex items-center gap-2 font-mono text-xs font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 hover:text-[#E52E2E]"
          >
            ALL DISPATCHES
            <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-12 border-t border-[#282D37]">
        {latest.map((a, i) => (
          <Reveal key={a.slug} delay={0.06 * i}>
            <Link
              to={`/journal/${a.slug}`}
              data-testid={`dispatch-card-${a.slug}`}
              className="group relative flex items-center gap-6 border-b border-[#282D37] px-2 py-8 transition-colors duration-500 hover:bg-[#121418] sm:gap-10 sm:px-6"
            >
              <span className="hidden font-mono text-sm tracking-[0.3em] text-[#3A404E] transition-colors duration-500 group-hover:text-[#E52E2E] sm:block">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-mono text-[10px] tracking-[0.3em]">
                  <span className="text-[#E52E2E]">{a.category}</span>
                  <span className="text-[#5F6573]">{a.date}</span>
                </div>
                <h3 className="mt-2.5 font-display text-2xl font-bold uppercase leading-tight tracking-wide text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E] sm:text-3xl">
                  {a.title}
                </h3>
              </div>
              <div className="relative hidden h-20 w-32 shrink-0 overflow-hidden border border-[#282D37] md:block">
                <img
                  src={a.image}
                  alt=""
                  className="h-full w-full object-cover opacity-80 saturate-[0.85] transition-[transform,opacity] duration-700 group-hover:scale-105 group-hover:opacity-100"
                />
              </div>
              <ArrowUpRight
                size={22}
                className="shrink-0 text-[#3A404E] transition-all duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 group-hover:text-[#E52E2E]"
              />
              <span className="absolute bottom-0 left-0 h-px w-0 bg-[#E52E2E] transition-[width] duration-700 group-hover:w-full" />
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const FoundryTeaser = () => (
  <section data-testid="foundry-teaser" className="border-t border-[#282D37]/70">
    <div className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10">
      <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">THE FOUNDRY // PLUGIN DEVELOPMENT</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            Most of the forge runs on Rust
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[#9EA4B0]">
            The majority of the studio's craft today is custom Oxide plugin development — a
            proprietary suite of statistics, identity, clan and live-operations systems powering
            every server on the Arsenal Rust network.
          </p>
          <Link
            to="/plugins"
            data-testid="foundry-teaser-cta"
            className="group mt-8 inline-flex items-center gap-3 border border-[#3A404E] px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-[#F4F5F7] transition-colors duration-300 hover:border-[#E52E2E] hover:text-[#E52E2E]"
          >
            Enter the Foundry
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="grid grid-cols-2 gap-px border border-[#282D37] bg-[#282D37]">
            {[
              ["ECOSYSTEM", "OXIDE / RUST"],
              ["SUITE", "8+ PROPRIETARY SYSTEMS"],
              ["DEPLOYED", "5-SERVER NETWORK"],
              ["MARKETPLACE", "IN CONCEPT"],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#121418] px-6 py-6">
                <p className="font-mono text-[9px] tracking-[0.3em] text-[#5F6573]">{k}</p>
                <p className="mt-1.5 font-mono text-xs font-bold tracking-widest text-[#F4F5F7]">{v}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const MoreWorlds = () => (
  <section data-testid="more-worlds" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
    <div className="mx-auto max-w-[1400px] px-6 py-20 lg:px-10">
      <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.35em] text-[#E52E2E]">FORGE PROJECTS</p>
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-tight tracking-tight text-[#F4F5F7] sm:text-5xl">
            Built for more than one world
          </h2>
          <div className="mt-7 flex flex-wrap gap-3">
            {["RUST PLUGINS", "WORLD OF WARCRAFT ADDONS"].map((g) => (
              <span key={g} className="border border-[#282D37] bg-[#121418] px-5 py-2.5 font-mono text-[11px] font-bold tracking-[0.25em] text-[#9EA4B0]">
                {g}
              </span>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <Link
            to="/downloads/wow/arsenal-hub"
            data-testid="latest-release-card"
            className="group flex items-center justify-between gap-6 border border-[#282D37] bg-[#121418] p-6 transition-[border-color] duration-500 hover:border-[#E52E2E]/60 sm:p-7"
          >
            <div className="flex items-center gap-5">
              <img src="/arsenalhub/arsenal-hub-logo.png" alt="Arsenal Hub emblem" className="w-11" />
              <div>
                <p className="font-mono text-[9px] tracking-[0.3em] text-[#E52E2E]">LATEST RELEASE // GUILD RELEASE</p>
                <p className="mt-1.5 font-display text-2xl font-bold uppercase tracking-wide text-[#F4F5F7] transition-colors duration-300 group-hover:text-[#E52E2E]">
                  Arsenal Hub <span className="text-[#5F6573]">v3.14.5</span>
                </p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-[#9EA4B0]">WOW FOREVER · GUILD TOOLS · GUIDE INCLUDED</p>
              </div>
            </div>
            <ArrowRight size={18} className="shrink-0 text-[#9EA4B0] transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#E52E2E]" />
          </Link>
        </Reveal>
      </div>
    </div>
  </section>
);

const FamilyStrip = () => (
  <section data-testid="family-strip" className="border-t border-[#282D37]/70 bg-[#0D0F12]">
    <div className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10">
      <Reveal>
        <a
          href="https://arsenalservers.co"
          target="_blank"
          rel="noreferrer"
          data-testid="family-strip-link"
          className="group flex flex-col items-start justify-between gap-6 border border-[#282D37] bg-[#121418] px-7 py-6 transition-[border-color] duration-500 hover:border-[#E52E2E]/50 sm:flex-row sm:items-center sm:px-10"
        >
          <div className="flex items-center gap-5">
            <img
              src="https://arsenalservers.co/arsenal-emblem.png"
              alt="Arsenal Servers emblem"
              className="w-10 transition-transform duration-500 group-hover:scale-110"
            />
            <div>
              <p className="font-mono text-[10px] tracking-[0.35em] text-[#E52E2E]">THE ARSENAL FAMILY</p>
              <p className="mt-1.5 font-display text-xl font-bold uppercase tracking-wide text-[#F4F5F7] sm:text-2xl">
                Arsenal Servers <span className="text-[#5F6573]">— the network side of the arsenal</span>
              </p>
            </div>
          </div>
          <span className="flex shrink-0 items-center gap-2 font-mono text-[11px] font-bold tracking-[0.25em] text-[#9EA4B0] transition-colors duration-300 group-hover:text-[#E52E2E]">
            ARSENALSERVERS.CO
            <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </a>
      </Reveal>
    </div>
  </section>
);

const Home = () => (
  <>
    <Hero />
    <Marquee />
    <Capabilities />
    <TerritoriaSpotlight />
    <CraftPillars />
    <FoundryTeaser />
    <MoreWorlds />
    <LatestDispatches />
    <FamilyStrip />
  </>
);

export default Home;
